import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Toast } from "primereact/toast";
import OutwardRequestsTab from "./OutwardRequestsTab";
import InwardRequestsTab from "./InwardRequestsTab";
import posService from "../../services/pos/pos.service";

interface IncomingRequestsDialogProps {
  visible: boolean;
  onHide: () => void;
  selectedPosIds: number[];
  onDataRefresh?: () => void;
}

const IncomingRequestsDialog: React.FC<IncomingRequestsDialogProps> = ({
  visible,
  onHide,
  selectedPosIds,
  onDataRefresh,
}) => {
  const toast = useRef<Toast>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [outwardRequests, setOutwardRequests] = useState<any[]>([]);
  const [inwardRequests, setInwardRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchOutwardRequests = async () => {
    if (!selectedPosIds || selectedPosIds.length === 0) return;

    try {
      setLoading(true);
      const [requestsResponse, stockResponse] = await Promise.all([
        posService.Getposrequest(selectedPosIds),
        posService.getProductStockSummary(selectedPosIds)
      ]);

      if (requestsResponse && Array.isArray(requestsResponse)) {
        // Create a map of product stock data for quick lookup
        const stockMap = new Map<string, any>();

        if (stockResponse && Array.isArray(stockResponse)) {
          stockResponse.forEach((stock: any) => {
            const key = `${stock.productId}-${stock.posId}`;
            stockMap.set(key, stock);
          });
        }

        // Merge stock data with requests
        const enrichedRequests = requestsResponse.map((request: any) => {
          const key = `${request.productId}-${request.requestToPOS}`;
          const stockData = stockMap.get(key);
          return {
            ...request,
            availableStock: stockData?.availableStock ?? 0,
            openOrderDemand: stockData?.openOrderDemand ?? 0,
            availableForMovements: stockData?.availableStockForOrder ?? 0,
          };
        });

        setOutwardRequests(enrichedRequests);
      } else {
        setOutwardRequests([]);
      }
    } catch (error) {
      console.error("Error fetching outward requests:", error);
      showMessage("Error", "Failed to load outward requests", "error");
      setOutwardRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInwardRequests = async () => {
    if (!selectedPosIds || selectedPosIds.length === 0) return;

    try {
      setLoading(true);
      const response = await posService.Getnewposinwardrequests(selectedPosIds);
      
      if (response && Array.isArray(response)) {
        setInwardRequests(response);
      } else {
        setInwardRequests([]);
      }
    } catch (error) {
      console.error("Error fetching inward requests:", error);
      showMessage("Error", "Failed to load inward requests", "error");
      setInwardRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchOutwardRequests();
      fetchInwardRequests();
    }
  }, [visible, selectedPosIds]);

  const handleRefresh = () => {
    fetchOutwardRequests();
    fetchInwardRequests();
    // Refresh main product stock data after accepting/rejecting requests
    onDataRefresh?.();
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Incoming Requests"
        modal
        style={{ width: "80vw" }}
        breakpoints={{ "960px": "90vw", "641px": "95vw" }}
      >
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header="Outward Requests">
            <OutwardRequestsTab
              data={outwardRequests}
              loading={loading}
              onRefresh={handleRefresh}
              selectedPosIds={selectedPosIds}
            />
          </TabPanel>
          
          <TabPanel header="Inward Requests">
            <InwardRequestsTab
              data={inwardRequests}
              loading={loading}
              onRefresh={handleRefresh}
              showMessage={showMessage}
              selectedPosIds={selectedPosIds}
            />
          </TabPanel>
        </TabView>
      </Dialog>
    </>
  );
};

export default IncomingRequestsDialog;
